import { useSetRecoilState } from 'recoil';
import { APIS, RouteConstants, isAdminConsoleUser } from '../constants';
import { useFetchWrapper, isPasswordChangeRequired, withPasswordUpdated } from 'helpers';
import { authState, User } from 'states';
import { useNavigate, useLocation } from 'react-router-dom';

const useUserService = () => {
  const baseUrl = `${process.env.REACT_APP_BASE_API_URL}`;
  const fetchWrapper = useFetchWrapper();
  const setAuth = useSetRecoilState(authState);
  const navigate = useNavigate();
  const location = useLocation();

  const login = (username: string, password: string) => {
    return fetchWrapper
      .post<User>(new URL(`${baseUrl}${APIS.USER.LOGIN}`), { username, password })
      .then((user) => {
        if (!isAdminConsoleUser(user)) {
          throw new Error('Access Denied. This portal is restricted to administrators only.');
        }

        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('user', JSON.stringify(user));
        setAuth(user);

        // still on the default onboarding password: must set a new one first
        if (isPasswordChangeRequired(user)) {
          navigate(RouteConstants.changePassword, { state: { forced: true }, replace: true });
          return;
        }

        // get return url from location state or default to home page
        const { from } = { from: { pathname: location.pathname || RouteConstants.dashboards } };
        navigate(from);
      });
  };

  // After the default password is changed: clear the flag in both the stored
  // session and the auth state, so the route guard stops forcing Change password.
  const markPasswordUpdated = (user: User) => {
    const updatedUser = withPasswordUpdated(user);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setAuth(updatedUser);
  };

  const logout = () => {
    // remove user from local storage, set auth state to null and redirect to login page
    localStorage.removeItem('user');
    setAuth({} as User);
    navigate(RouteConstants.login);
  };

  const getUsers = (showRoles: boolean) => {
    let getUserUrl = `${baseUrl}${APIS.USER.GET_ALL_USERS}`;
    if (showRoles) {
      getUserUrl = `${getUserUrl}?showRoles=true`;
    }
    return fetchWrapper.get<User[]>(new URL(getUserUrl));
  };

  const getUsersForRole = (roleId: number) => {
    return fetchWrapper.get<User[]>(new URL(`${baseUrl}${APIS.USER.GET_ALL_USERS}?roleId=${roleId}`));
  };

  const createOrUpdateUser = (userDetail: User) => {
    return fetchWrapper.post(new URL(`${baseUrl}${APIS.USER.ADD_UPDATE_USER}`), userDetail);
  };

  return {
    login,
    logout,
    markPasswordUpdated,
    getUsers,
    getUsersForRole,
    createOrUpdateUser,
  };
};

export { useUserService };
